/**
 * SuperAdminSubscriptions.tsx
 *
 * Unified Control Panel for Super Admins to manage:
 *   Tab 1 — Admin Operators  (view / grant / renew / suspend admin subscriptions)
 *   Tab 2 — Retail Users     (view / grant / renew / suspend retail subscriptions)
 *   Tab 3 — Plans & Pricing  (edit retail + admin plan prices and renew/upgrade payment methods)
 *   Tab 4 — Revenue Dashboard (overview counts)
 */

import React, { useState, useEffect, useCallback } from 'react';
import { adminApiService } from '../../services/adminApiService';
import './AdminComponents.css';

// ─── Types ────────────────────────────────────────────────────────────────────

interface SubscriptionPlan {
  id: number;
  name: string;
  priceMonthly: number;
  currency: string;
  maxStrategies: number;
  /** ALL | STRIPE | CRYPTO — renew/upgrade checkout options for this retail plan. */
  registrationPaymentMethods?: string | null;
  registration_payment_methods?: string | null;
  /** JSON from subscription_plans.features; planKind=registration_product = product tier (not bot strategies). */
  features?: string | null;
  active: boolean;
}

interface AdminSubscriptionPlan extends SubscriptionPlan {
  maxRetailUsers: number;
  maxPositions: number;
}

interface RetailUserRow {
  userId: number;
  username: string;
  email: string;
  subscriptionStatus: 'ACTIVE' | 'GRACE_PERIOD' | 'SUSPENDED' | 'EXPIRED' | 'NONE';
  planName: string | null;
  expiresAt: string | null;
  subscriptionId: number | null;
}

interface AdminOperatorRow {
  adminUserId: number;
  username: string;
  email: string;
  role: string | null;
  subscriptionStatus: 'ACTIVE' | 'GRACE_PERIOD' | 'SUSPENDED' | 'EXPIRED' | 'NONE';
  planName: string | null;
  expiresAt: string | null;
  subscriptionId: number | null;
}

interface RevenueSummary {
  activeRetailSubscribers: number;
  activeAdminSubscribers: number;
  retailPlans: number;
  adminPlans: number;
  // Extended fields (returned by the API when available)
  adminMrr?: number;
  retailMrr?: number;
  totalMrr?: number;
  adminGraceCount?: number;
  retailGraceCount?: number;
  adminSuspendedCount?: number;
  retailSuspendedCount?: number;
  renewalsDueThisMonth?: number;
  atRiskCount?: number;
}

// Flat payment row used in the Revenue tab ledger
interface AllPaymentRow {
  id: number;
  amount: number;
  currency: string;
  paymentDate: string;
  paymentMethod: string | null;
  transactionId: string | null;
  status: string;
  notes: string | null;
  username: string;
  email: string;
  kind: 'Retail' | 'Admin';
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const STATUS_COLORS: Record<string, string> = {
  ACTIVE:       'bg-green-100 text-green-800',
  GRACE_PERIOD: 'bg-yellow-100 text-yellow-800',
  SUSPENDED:    'bg-orange-100 text-orange-800',
  EXPIRED:      'bg-red-100 text-red-800',
  NONE:         'bg-gray-100 text-gray-600',
};

function parsePlanFeatures(features?: string | null): Record<string, unknown> | null {
  if (!features || typeof features !== 'string') return null;
  try {
    return JSON.parse(features) as Record<string, unknown>;
  } catch {
    return null;
  }
}

function isRegistrationProductPlan(p: SubscriptionPlan): boolean {
  return parsePlanFeatures(p.features)?.planKind === 'registration_product';
}

/** Dropdown label for retail plans (includes product-registration tiers). */
function formatRetailPlanSelectLabel(p: SubscriptionPlan): string {
  const price = `€${Number(p.priceMonthly).toFixed(2)}`;
  if (isRegistrationProductPlan(p)) {
    return `${p.name} — ${price}/mo (product tier · not a strategy plan)`;
  }
  return `${p.name} — ${price}/month`;
}

/** Normalized renew/upgrade payment mode for plan row (Super Admin Plans tab + API). */
function planRegistrationPaymentMethodsSelect(p: {
  registrationPaymentMethods?: string | null;
  registration_payment_methods?: string | null;
}): 'ALL' | 'STRIPE' | 'CRYPTO' {
  const raw = String(p.registrationPaymentMethods ?? p.registration_payment_methods ?? 'ALL').toUpperCase();
  if (raw === 'STRIPE' || raw === 'CRYPTO' || raw === 'ALL') return raw;
  return 'ALL';
}

/** Subtitle under plan name on Plans & Pricing tab. */
function formatRetailPlanCardSubtitle(p: SubscriptionPlan): string {
  const price = `€${Number(p.priceMonthly).toFixed(2)}/month`;
  if (isRegistrationProductPlan(p)) {
    return `${price} · Registration / market-signals product tier (0 strategy slots)`;
  }
  return `${price} · Max ${p.maxStrategies} strategies`;
}

function formatAdminPlanSelectLabel(p: AdminSubscriptionPlan): string {
  return `${p.name} — €${Number(p.priceMonthly).toFixed(2)}/month`;
}

function StatusBadge({ status }: { status: string }) {
  const cls = STATUS_COLORS[status] || 'bg-gray-100 text-gray-600';
  return (
    <span style={{
      padding: '2px 10px',
      borderRadius: 12,
      fontSize: 11,
      fontWeight: 600,
      background: status === 'ACTIVE' ? '#d1fae5' :
                  status === 'GRACE_PERIOD' ? '#fef9c3' :
                  status === 'SUSPENDED' ? '#ffedd5' :
                  status === 'EXPIRED' ? '#fee2e2' : '#f3f4f6',
      color: status === 'ACTIVE' ? '#065f46' :
             status === 'GRACE_PERIOD' ? '#92400e' :
             status === 'SUSPENDED' ? '#9a3412' :
             status === 'EXPIRED' ? '#991b1b' : '#6b7280',
    }}>
      {status === 'NONE' ? 'No Subscription' : status.replace('_', ' ')}
    </span>
  );
}

// ─── Grant / Renew Modal ──────────────────────────────────────────────────────

interface GrantModalProps {
  title: string;
  plans: SubscriptionPlan[] | AdminSubscriptionPlan[];
  onConfirm: (planId: number, months: number) => Promise<void>;
  onClose: () => void;
}

function GrantModal({ title, plans, onConfirm, onClose }: GrantModalProps) {
  const [planId, setPlanId] = useState<number>(plans[0]?.id || 0);
  const [months, setMonths] = useState(1);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleConfirm() {
    setSaving(true);
    setError(null);
    try {
      await onConfirm(planId, months);
      onClose();
    } catch (e: any) {
      setError(e?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
    }}>
      <div style={{
        background: '#fff', borderRadius: 12, padding: 28,
        width: 400, maxWidth: '90vw', boxShadow: '0 20px 60px rgba(0,0,0,0.2)'
      }}>
        <h3 style={{ margin: '0 0 20px', fontSize: 18, fontWeight: 700 }}>{title}</h3>

        <label style={{ display: 'block', marginBottom: 6, fontSize: 13, fontWeight: 600, color: '#374151' }}>
          Plan
        </label>
        <select
          value={planId}
          onChange={e => setPlanId(Number(e.target.value))}
          style={{
            width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db',
            fontSize: 14, marginBottom: 16, outline: 'none'
          }}
        >
          {plans.map(p => (
            <option key={p.id} value={p.id}>
              {'maxRetailUsers' in p
                ? formatAdminPlanSelectLabel(p as AdminSubscriptionPlan)
                : formatRetailPlanSelectLabel(p as SubscriptionPlan)}
            </option>
          ))}
        </select>

        <label style={{ display: 'block', marginBottom: 6, fontSize: 13, fontWeight: 600, color: '#374151' }}>
          Duration (months)
        </label>
        <select
          value={months}
          onChange={e => setMonths(Number(e.target.value))}
          style={{
            width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db',
            fontSize: 14, marginBottom: 20, outline: 'none'
          }}
        >
          {[1, 3, 6, 12].map(m => (
            <option key={m} value={m}>{m} month{m > 1 ? 's' : ''}</option>
          ))}
        </select>

        {error && (
          <p style={{ color: '#dc2626', fontSize: 13, marginBottom: 12 }}>{error}</p>
        )}

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            style={{
              padding: '8px 20px', borderRadius: 20, border: '1px solid #d1d5db',
              background: '#fff', cursor: 'pointer', fontSize: 14
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={saving || !planId}
            style={{
              padding: '8px 20px', borderRadius: 20, border: 'none',
              background: saving ? '#9ca3af' : '#086afb', color: '#fff',
              cursor: saving ? 'not-allowed' : 'pointer', fontSize: 14, fontWeight: 600
            }}
          >
            {saving ? 'Saving…' : 'Confirm'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Change Plan Modal (R-F04 / R-F05) ───────────────────────────────────────

interface ChangePlanModalProps {
  title: string;
  currentPlanName: string | null;
  plans: SubscriptionPlan[] | AdminSubscriptionPlan[];
  onConfirm: (planId: number) => Promise<void>;
  onClose: () => void;
}

function ChangePlanModal({ title, currentPlanName, plans, onConfirm, onClose }: ChangePlanModalProps) {
  const [planId, setPlanId] = useState<number>(plans[0]?.id || 0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleConfirm() {
    setSaving(true);
    setError(null);
    try {
      await onConfirm(planId);
      onClose();
    } catch (e: any) {
      setError(e?.message || 'Failed to change plan');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
    }}>
      <div style={{
        background: '#fff', borderRadius: 12, padding: 28,
        width: 400, maxWidth: '90vw', boxShadow: '0 20px 60px rgba(0,0,0,0.2)'
      }}>
        <h3 style={{ margin: '0 0 8px', fontSize: 18, fontWeight: 700 }}>{title}</h3>
        {currentPlanName && (
          <p style={{ color: '#6b7280', fontSize: 13, marginBottom: 20 }}>
            Current plan: <strong>{currentPlanName}</strong>
          </p>
        )}

        <label style={{ display: 'block', marginBottom: 6, fontSize: 13, fontWeight: 600, color: '#374151' }}>
          New Plan
        </label>
        <select
          value={planId}
          onChange={e => setPlanId(Number(e.target.value))}
          style={{
            width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db',
            fontSize: 14, marginBottom: 20, outline: 'none'
          }}
        >
          {plans.map(p => (
            <option key={p.id} value={p.id}>
              {'maxRetailUsers' in p
                ? formatAdminPlanSelectLabel(p as AdminSubscriptionPlan)
                : formatRetailPlanSelectLabel(p as SubscriptionPlan)}
            </option>
          ))}
        </select>

        <p style={{ color: '#6b7280', fontSize: 12, marginBottom: 20, background: '#f9fafb', padding: '8px 12px', borderRadius: 6 }}>
          ℹ️ The plan change takes effect immediately. The new billing amount applies at the next renewal cycle.
        </p>

        {error && (
          <p style={{ color: '#dc2626', fontSize: 13, marginBottom: 12 }}>{error}</p>
        )}

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            style={{
              padding: '8px 20px', borderRadius: 20, border: '1px solid #d1d5db',
              background: '#fff', cursor: 'pointer', fontSize: 14
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={saving || !planId}
            style={{
              padding: '8px 20px', borderRadius: 20, border: 'none',
              background: saving ? '#9ca3af' : '#086afb', color: '#fff',
              cursor: saving ? 'not-allowed' : 'pointer', fontSize: 14, fontWeight: 600
            }}
          >
            {saving ? 'Changing…' : 'Change Plan'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Cancel Confirm Dialog (R-F06) ────────────────────────────────────────────

interface CancelConfirmProps {
  name: string;
  onConfirm: () => Promise<void>;
  onClose: () => void;
}

function CancelConfirmDialog({ name, onConfirm, onClose }: CancelConfirmProps) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleConfirm() {
    setSaving(true);
    setError(null);
    try {
      await onConfirm();
      onClose();
    } catch (e: any) {
      setError(e?.message || 'Failed to cancel subscription');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
    }}>
      <div style={{
        background: '#fff', borderRadius: 12, padding: 28,
        width: 380, maxWidth: '90vw', boxShadow: '0 20px 60px rgba(0,0,0,0.2)'
      }}>
        <h3 style={{ margin: '0 0 12px', fontSize: 18, fontWeight: 700, color: '#dc2626' }}>
          ⚠️ Cancel Subscription
        </h3>
        <p style={{ color: '#374151', fontSize: 14, marginBottom: 20, lineHeight: '1.5' }}>
          Are you sure you want to cancel the subscription for <strong>{name}</strong>?
          This will immediately set the status to <strong>CANCELLED</strong> and stop all future renewals.
        </p>

        {error && (
          <p style={{ color: '#dc2626', fontSize: 13, marginBottom: 12 }}>{error}</p>
        )}

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            style={{
              padding: '8px 20px', borderRadius: 20, border: '1px solid #d1d5db',
              background: '#fff', cursor: 'pointer', fontSize: 14
            }}
          >
            Keep Subscription
          </button>
          <button
            onClick={handleConfirm}
            disabled={saving}
            style={{
              padding: '8px 20px', borderRadius: 20, border: 'none',
              background: saving ? '#9ca3af' : '#dc2626', color: '#fff',
              cursor: saving ? 'not-allowed' : 'pointer', fontSize: 14, fontWeight: 600
            }}
          >
            {saving ? 'Cancelling…' : 'Yes, Cancel'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Price Edit Modal ─────────────────────────────────────────────────────────

interface EditPriceModalProps {
  plan: SubscriptionPlan | AdminSubscriptionPlan;
  onSave: (planId: number, price: number) => Promise<void>;
  onClose: () => void;
}

function EditPriceModal({ plan, onSave, onClose }: EditPriceModalProps) {
  const [price, setPrice] = useState(String(plan.priceMonthly));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSave() {
    const p = parseFloat(price);
    if (isNaN(p) || p <= 0) { setError('Enter a valid price'); return; }
    setSaving(true);
    setError(null);
    try {
      await onSave(plan.id, p);
      onClose();
    } catch (e: any) {
      setError(e?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
    }}>
      <div style={{
        background: '#fff', borderRadius: 12, padding: 28,
        width: 360, maxWidth: '90vw', boxShadow: '0 20px 60px rgba(0,0,0,0.2)'
      }}>
        <h3 style={{ margin: '0 0 16px', fontSize: 18, fontWeight: 700 }}>Edit Plan Price</h3>
        <p style={{ color: '#6b7280', fontSize: 14, marginBottom: 16 }}>
          Plan: <strong>{plan.name}</strong>
        </p>

        <label style={{ display: 'block', marginBottom: 6, fontSize: 13, fontWeight: 600, color: '#374151' }}>
          Monthly Price ({plan.currency})
        </label>
        <input
          type="number"
          step="0.01"
          min="0"
          value={price}
          onChange={e => setPrice(e.target.value)}
          style={{
            width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db',
            fontSize: 14, marginBottom: 20, outline: 'none', boxSizing: 'border-box'
          }}
        />

        {error && <p style={{ color: '#dc2626', fontSize: 13, marginBottom: 12 }}>{error}</p>}

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{
            padding: '8px 20px', borderRadius: 20, border: '1px solid #d1d5db',
            background: '#fff', cursor: 'pointer', fontSize: 14
          }}>Cancel</button>
          <button onClick={handleSave} disabled={saving} style={{
            padding: '8px 20px', borderRadius: 20, border: 'none',
            background: saving ? '#9ca3af' : '#086afb', color: '#fff',
            cursor: saving ? 'not-allowed' : 'pointer', fontSize: 14, fontWeight: 600
          }}>{saving ? 'Saving…' : 'Save Price'}</button>
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

const SuperAdminSubscriptions: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'operators' | 'retail' | 'plans' | 'revenue'>('operators');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  React.useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Data
  const [retailUsers, setRetailUsers] = useState<RetailUserRow[]>([]);
  const [operators, setOperators] = useState<AdminOperatorRow[]>([]);
  const [retailPlans, setRetailPlans] = useState<SubscriptionPlan[]>([]);
  const [adminPlans, setAdminPlans] = useState<AdminSubscriptionPlan[]>([]);
  const [revenueSummary, setRevenueSummary] = useState<RevenueSummary | null>(null);
  const [allPayments, setAllPayments] = useState<AllPaymentRow[]>([]);

  // Loading / error
  const [loadingRetail, setLoadingRetail] = useState(false);
  const [loadingOps, setLoadingOps] = useState(false);
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [loadingRevenue, setLoadingRevenue] = useState(false);
  const [loadingPayments, setLoadingPayments] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [savingPlanPaymentKey, setSavingPlanPaymentKey] = useState<string | null>(null);

  // Search
  const [searchRetail, setSearchRetail] = useState('');
  const [searchOps, setSearchOps] = useState('');

  // Modals
  const [grantRetailModal, setGrantRetailModal] = useState<RetailUserRow | null>(null);
  const [renewRetailModal, setRenewRetailModal] = useState<RetailUserRow | null>(null);
  const [changePlanRetailModal, setChangePlanRetailModal] = useState<RetailUserRow | null>(null);
  const [cancelRetailModal, setCancelRetailModal] = useState<RetailUserRow | null>(null);
  const [grantOpsModal, setGrantOpsModal] = useState<AdminOperatorRow | null>(null);
  const [renewOpsModal, setRenewOpsModal] = useState<AdminOperatorRow | null>(null);
  const [changePlanOpsModal, setChangePlanOpsModal] = useState<AdminOperatorRow | null>(null);
  const [cancelOpsModal, setCancelOpsModal] = useState<AdminOperatorRow | null>(null);
  const [editPriceModal, setEditPriceModal] = useState<{ plan: SubscriptionPlan | AdminSubscriptionPlan; type: 'retail' | 'admin' } | null>(null);
  const [historyModal, setHistoryModal] = useState<{ label: string; userId: number; kind: 'retail' | 'admin' } | null>(null);

  // ── Loaders ────────────────────────────────────────────────────────────

  const loadRetailUsers = useCallback(async () => {
    setLoadingRetail(true);
    setError(null);
    try {
      const res = await adminApiService.getSuperAdminRetailUsers();
      if (res.success && res.data) setRetailUsers(res.data as RetailUserRow[]);
      else setError(res.error || 'Failed to load retail users');
    } catch { setError('Failed to load retail users'); }
    finally { setLoadingRetail(false); }
  }, []);

  const loadOperators = useCallback(async () => {
    setLoadingOps(true);
    setError(null);
    try {
      const res = await adminApiService.getSuperAdminOperators();
      if (res.success && res.data) setOperators(res.data as AdminOperatorRow[]);
      else setError(res.error || 'Failed to load admin operators');
    } catch { setError('Failed to load admin operators'); }
    finally { setLoadingOps(false); }
  }, []);

  const loadPlans = useCallback(async () => {
    setLoadingPlans(true);
    try {
      const [rp, ap] = await Promise.all([
        adminApiService.getRetailPlans(),
        adminApiService.getAdminPlans(),
      ]);
      if (rp.success && rp.data) setRetailPlans(rp.data as SubscriptionPlan[]);
      if (ap.success && ap.data) setAdminPlans(ap.data as AdminSubscriptionPlan[]);
    } catch { setError('Failed to load plans'); }
    finally { setLoadingPlans(false); }
  }, []);

  const loadRevenue = useCallback(async () => {
    setLoadingRevenue(true);
    try {
      const res = await adminApiService.getSubscriptionRevenueSummary();
      if (res.success && res.data) setRevenueSummary(res.data as RevenueSummary);
    } catch { setError('Failed to load revenue summary'); }
    finally { setLoadingRevenue(false); }
  }, []);

  // Fetch actual payment records for every retail user + operator so the
  // Revenue tab can show the real collected transaction ledger (not just
  // projected MRR from current active plans).
  const loadAllPayments = useCallback(async (
    currentRetailUsers: RetailUserRow[],
    currentOperators: AdminOperatorRow[]
  ) => {
    setLoadingPayments(true);
    try {
      const retailFetches = currentRetailUsers.map(u =>
        adminApiService.getRetailUserPayments(u.userId)
          .then(r => (r.success && r.data ? (r.data as PaymentRecord[]).map(p => ({
            ...p, username: u.username, email: u.email, kind: 'Retail' as const,
          })) : []))
          .catch(() => [] as AllPaymentRow[])
      );
      const adminFetches = currentOperators.map(o =>
        adminApiService.getAdminOperatorPayments(o.adminUserId)
          .then(r => (r.success && r.data ? (r.data as PaymentRecord[]).map(p => ({
            ...p, username: o.username, email: o.email, kind: 'Admin' as const,
          })) : []))
          .catch(() => [] as AllPaymentRow[])
      );
      const results = await Promise.all([...retailFetches, ...adminFetches]);
      const flat = results.flat().sort((a, b) =>
        new Date(b.paymentDate).getTime() - new Date(a.paymentDate).getTime()
      );
      setAllPayments(flat);
    } catch { /* silently ignore — payments are supplementary */ }
    finally { setLoadingPayments(false); }
  }, []);

  // Load all tabs on mount (for quick switching)
  useEffect(() => {
    loadRetailUsers();
    loadOperators();
    loadPlans();
    loadRevenue();
  }, [loadRetailUsers, loadOperators, loadPlans, loadRevenue]);

  // Re-fetch revenue + actual payment ledger every time the Revenue tab is
  // activated so it always reflects the latest state.
  useEffect(() => {
    if (activeTab === 'revenue') {
      loadRevenue();
      loadAllPayments(retailUsers, operators);
    }
  }, [activeTab, loadRevenue, loadAllPayments, retailUsers, operators]);

  // ── Action helpers ─────────────────────────────────────────────────────

  function showSuccess(msg: string) {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(null), 4000);
  }

  async function handleGrantRetail(planId: number, months: number) {
    if (!grantRetailModal) return;
    await adminApiService.grantRetailSubscription(grantRetailModal.userId, planId, months);
    showSuccess(`Subscription granted to ${grantRetailModal.username}`);
    await Promise.all([loadRetailUsers(), loadRevenue()]);
  }

  async function handleRenewRetail(planId: number, months: number) {
    if (!renewRetailModal?.subscriptionId) return;
    await adminApiService.renewRetailSubscription(renewRetailModal.subscriptionId, months);
    showSuccess(`Subscription renewed for ${renewRetailModal.username}`);
    await Promise.all([loadRetailUsers(), loadRevenue()]);
  }

  async function handleSuspendRetail(user: RetailUserRow) {
    if (!window.confirm(`Suspend subscription for ${user.username}?`)) return;
    try {
      await adminApiService.suspendRetailSubscription(user.userId);
      showSuccess(`${user.username}'s subscription suspended`);
      await Promise.all([loadRetailUsers(), loadRevenue()]);
    } catch (e: any) { setError(e?.message || 'Failed to suspend'); }
  }

  async function handleGrantOps(planId: number, months: number) {
    if (!grantOpsModal) return;
    await adminApiService.grantAdminSubscription(grantOpsModal.adminUserId, planId, months);
    showSuccess(`Admin subscription granted to ${grantOpsModal.username}`);
    await Promise.all([loadOperators(), loadRevenue()]);
  }

  async function handleRenewOps(planId: number, months: number) {
    if (!renewOpsModal?.subscriptionId) return;
    await adminApiService.renewAdminSubscription(renewOpsModal.subscriptionId, months);
    showSuccess(`Admin subscription renewed for ${renewOpsModal.username}`);
    await Promise.all([loadOperators(), loadRevenue()]);
  }

  async function handleSuspendOps(op: AdminOperatorRow) {
    if (!window.confirm(`Suspend admin subscription for ${op.username}?`)) return;
    try {
      await adminApiService.suspendAdminSubscription(op.adminUserId);
      showSuccess(`${op.username}'s admin subscription suspended`);
      await Promise.all([loadOperators(), loadRevenue()]);
    } catch (e: any) { setError(e?.message || 'Failed to suspend'); }
  }

  // R-F06 cancel handlers
  async function handleCancelRetail() {
    if (!cancelRetailModal) return;
    await adminApiService.cancelRetailSubscription(cancelRetailModal.userId);
    showSuccess(`${cancelRetailModal.username}'s subscription has been cancelled`);
    await Promise.all([loadRetailUsers(), loadRevenue()]);
  }

  async function handleCancelOps() {
    if (!cancelOpsModal) return;
    await adminApiService.cancelAdminSubscription(cancelOpsModal.adminUserId);
    showSuccess(`${cancelOpsModal.username}'s admin subscription has been cancelled`);
    await Promise.all([loadOperators(), loadRevenue()]);
  }

  // R-F04/R-F05 change plan handlers
  async function handleChangePlanRetail(planId: number) {
    if (!changePlanRetailModal) return;
    await adminApiService.changeRetailUserPlan(changePlanRetailModal.userId, planId);
    showSuccess(`Plan changed for ${changePlanRetailModal.username}`);
    await Promise.all([loadRetailUsers(), loadRevenue()]);
  }

  async function handleChangePlanOps(planId: number) {
    if (!changePlanOpsModal) return;
    await adminApiService.changeAdminOperatorPlan(changePlanOpsModal.adminUserId, planId);
    showSuccess(`Admin plan changed for ${changePlanOpsModal.username}`);
    await Promise.all([loadOperators(), loadRevenue()]);
  }

  async function handleSavePrice(planId: number, price: number) {
    if (!editPriceModal) return;
    if (editPriceModal.type === 'retail') {
      await adminApiService.updateRetailPlanPrice(planId, price);
    } else {
      await adminApiService.updateAdminPlanPrice(planId, price);
    }
    showSuccess('Plan price updated');
    await loadPlans();
  }

  async function handleRetailPlanPaymentMethods(planId: number, value: string) {
    setError(null);
    const normalized = planRegistrationPaymentMethodsSelect({ registrationPaymentMethods: value });
    setSavingPlanPaymentKey(`retail-${planId}`);
    try {
      const res = await adminApiService.updateRetailPlanRegistrationPaymentMethods(planId, normalized);
      if (!res.success) {
        setError(res.error || 'Failed to update retail plan payment methods');
        await loadPlans();
        return;
      }
      showSuccess('Retail plan renew/upgrade payment options updated.');
      await loadPlans();
    } catch (e: any) {
      setError(e?.message || 'Failed to update retail plan payment methods');
      await loadPlans();
    } finally {
      setSavingPlanPaymentKey(null);
    }
  }

  async function handleAdminPlanPaymentMethods(planId: number, value: string) {
    setError(null);
    const normalized = planRegistrationPaymentMethodsSelect({ registrationPaymentMethods: value });
    setSavingPlanPaymentKey(`admin-${planId}`);
    try {
      const res = await adminApiService.updateAdminPlanRegistrationPaymentMethods(planId, normalized);
      if (!res.success) {
        setError(res.error || 'Failed to update admin plan payment methods');
        await loadPlans();
        return;
      }
      showSuccess('Admin plan renewal payment options updated.');
      await loadPlans();
    } catch (e: any) {
      setError(e?.message || 'Failed to update admin plan payment methods');
      await loadPlans();
    } finally {
      setSavingPlanPaymentKey(null);
    }
  }

  // ── Filtered lists ─────────────────────────────────────────────────────

  const filteredRetail = retailUsers.filter(u =>
    !searchRetail || u.username.toLowerCase().includes(searchRetail.toLowerCase()) ||
    u.email.toLowerCase().includes(searchRetail.toLowerCase())
  );

  const filteredOps = operators.filter(o =>
    !searchOps || o.username.toLowerCase().includes(searchOps.toLowerCase()) ||
    (o.email || '').toLowerCase().includes(searchOps.toLowerCase())
  );

  // ── Render ─────────────────────────────────────────────────────────────

  const tabStyle = (tab: typeof activeTab): React.CSSProperties => ({
    padding: isMobile ? '8px 12px' : '10px 20px',
    border: 'none',
    borderBottom: activeTab === tab ? '3px solid #086afb' : '3px solid transparent',
    background: 'none',
    cursor: 'pointer',
    fontSize: isMobile ? 12 : 14,
    fontWeight: activeTab === tab ? 700 : 500,
    color: activeTab === tab ? '#086afb' : '#6b7280',
    transition: 'all 0.15s',
    whiteSpace: 'nowrap',
  });

  return (
    <div style={{ padding: isMobile ? '16px 12px' : '24px', maxWidth: 1200, margin: '0 auto' }}>

      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#111827' }}>
          🔑 Super-Admin: Subscription Control Panel
        </h2>
        <p style={{ color: '#6b7280', marginTop: 4, fontSize: 14 }}>
          Manage retail user subscriptions, admin operator access, plan pricing, and renew/upgrade payment options.
        </p>
      </div>

      {/* Global alerts */}
      {error && (
        <div style={{
          background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 8,
          padding: '12px 16px', marginBottom: 16, color: '#991b1b', fontSize: 14
        }}>
          ⚠️ {error}
          <button onClick={() => setError(null)} style={{
            float: 'right', background: 'none', border: 'none', cursor: 'pointer', color: '#991b1b'
          }}>✕</button>
        </div>
      )}
      {successMsg && (
        <div style={{
          background: '#d1fae5', border: '1px solid #6ee7b7', borderRadius: 8,
          padding: '12px 16px', marginBottom: 16, color: '#065f46', fontSize: 14
        }}>
          ✅ {successMsg}
        </div>
      )}

      {/* Tabs */}
      <div style={{
        borderBottom: '1px solid #e5e7eb', marginBottom: 24,
        display: 'flex', gap: 0,
        overflowX: 'auto', WebkitOverflowScrolling: 'touch' as any,
        msOverflowStyle: 'none', scrollbarWidth: 'none' as any,
      }}>
        <button style={tabStyle('operators')} onClick={() => setActiveTab('operators')}>
          {isMobile ? '👥 Operators' : '👥 Admin Operators'}
        </button>
        <button style={tabStyle('retail')} onClick={() => setActiveTab('retail')}>
          {isMobile ? '🛒 Retail' : '🛒 Retail Users'}
        </button>
        <button style={tabStyle('plans')} onClick={() => setActiveTab('plans')}>
          {isMobile ? '💰 Plans' : '💰 Plans & Pricing'}
        </button>
        <button style={tabStyle('revenue')} onClick={() => setActiveTab('revenue')}>
          📊 Revenue
        </button>
      </div>

      {/* ── TAB: Admin Operators ───────────────────────────────── */}
      {activeTab === 'operators' && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
            <input
              placeholder="Search operators…"
              value={searchOps}
              onChange={e => setSearchOps(e.target.value)}
              style={{
                padding: '8px 14px', borderRadius: 8, border: '1px solid #d1d5db',
                fontSize: 14, flex: 1, minWidth: 0, outline: 'none'
              }}
            />
            <button onClick={loadOperators} style={{
              padding: '8px 16px', borderRadius: 20, background: '#f3f4f6',
              border: '1px solid #d1d5db', cursor: 'pointer', fontSize: 13, whiteSpace: 'nowrap'
            }}>🔄 Refresh</button>
          </div>

          {loadingOps ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}>Loading…</div>
          ) : isMobile ? (
            /* ── Mobile card list ── */
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {filteredOps.length === 0 && (
                <div style={{ textAlign: 'center', padding: 32, color: '#9ca3af' }}>No operators found</div>
              )}
              {filteredOps.map(op => (
                <div key={op.adminUserId} style={{
                  background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12,
                  padding: '14px 16px', boxShadow: '0 1px 4px rgba(0,0,0,0.04)'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 15 }}>{op.username}</div>
                      <div style={{ fontSize: 12, color: '#6b7280' }}>{op.email}</div>
                    </div>
                    <StatusBadge status={op.subscriptionStatus} />
                  </div>
                  <div style={{ display: 'flex', gap: 16, marginBottom: 10, fontSize: 13 }}>
                    <div><span style={{ color: '#9ca3af' }}>Role: </span><strong>{op.role || '—'}</strong></div>
                    <div><span style={{ color: '#9ca3af' }}>Plan: </span><strong>{op.planName || '—'}</strong></div>
                  </div>
                  {op.expiresAt && (
                    <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 10 }}>
                      Expires: {new Date(op.expiresAt).toLocaleDateString()}
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    <button onClick={() => setGrantOpsModal(op)} style={btnBlue}>Grant</button>
                    {op.subscriptionId && (
                      <>
                        <button onClick={() => setRenewOpsModal(op)} style={btnGreen}>Renew</button>
                        <button onClick={() => setChangePlanOpsModal(op)} style={btnOrange}>Change Plan</button>
                      </>
                    )}
                    {(op.subscriptionStatus === 'ACTIVE' || op.subscriptionStatus === 'GRACE_PERIOD') && (
                      <button onClick={() => handleSuspendOps(op)} style={btnRed}>Suspend</button>
                    )}
                    {(op.subscriptionStatus === 'ACTIVE' || op.subscriptionStatus === 'GRACE_PERIOD' || op.subscriptionStatus === 'SUSPENDED') && (
                      <button onClick={() => setCancelOpsModal(op)} style={btnGray}>Cancel</button>
                    )}
                    <button onClick={() => setHistoryModal({ label: op.username, userId: op.adminUserId, kind: 'admin' })} style={btnHistory}>History</button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            /* ── Desktop table ── */
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                <thead>
                  <tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                    <th style={th}>Username</th>
                    <th style={th}>Email</th>
                    <th style={th}>Role</th>
                    <th style={th}>Plan</th>
                    <th style={th}>Status</th>
                    <th style={th}>Expires</th>
                    <th style={th}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredOps.map(op => (
                    <tr key={op.adminUserId} style={{ borderBottom: '1px solid #f3f4f6' }}>
                      <td style={td}><strong>{op.username}</strong></td>
                      <td style={td}>{op.email}</td>
                      <td style={td}>{op.role || '—'}</td>
                      <td style={td}>{op.planName || '—'}</td>
                      <td style={td}><StatusBadge status={op.subscriptionStatus} /></td>
                      <td style={td}>{op.expiresAt ? new Date(op.expiresAt).toLocaleDateString() : '—'}</td>
                      <td style={td}>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          <button onClick={() => setGrantOpsModal(op)} style={btnBlue}>Grant</button>
                          {op.subscriptionId && (
                            <>
                              <button onClick={() => setRenewOpsModal(op)} style={btnGreen}>Renew</button>
                              <button onClick={() => setChangePlanOpsModal(op)} style={btnOrange}>Change Plan</button>
                            </>
                          )}
                          {(op.subscriptionStatus === 'ACTIVE' || op.subscriptionStatus === 'GRACE_PERIOD') && (
                            <button onClick={() => handleSuspendOps(op)} style={btnRed}>Suspend</button>
                          )}
                          {(op.subscriptionStatus === 'ACTIVE' || op.subscriptionStatus === 'GRACE_PERIOD' || op.subscriptionStatus === 'SUSPENDED') && (
                            <button onClick={() => setCancelOpsModal(op)} style={btnGray}>Cancel</button>
                          )}
                          <button onClick={() => setHistoryModal({ label: op.username, userId: op.adminUserId, kind: 'admin' })} style={btnHistory}>History</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {filteredOps.length === 0 && (
                    <tr><td colSpan={7} style={{ textAlign: 'center', padding: 32, color: '#9ca3af' }}>No operators found</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── TAB: Retail Users ─────────────────────────────────── */}
      {activeTab === 'retail' && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
            <input
              placeholder="Search users…"
              value={searchRetail}
              onChange={e => setSearchRetail(e.target.value)}
              style={{
                padding: '8px 14px', borderRadius: 8, border: '1px solid #d1d5db',
                fontSize: 14, flex: 1, minWidth: 0, outline: 'none'
              }}
            />
            <button onClick={loadRetailUsers} style={{
              padding: '8px 16px', borderRadius: 20, background: '#f3f4f6',
              border: '1px solid #d1d5db', cursor: 'pointer', fontSize: 13, whiteSpace: 'nowrap'
            }}>🔄 Refresh</button>
          </div>

          {loadingRetail ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}>Loading…</div>
          ) : isMobile ? (
            /* ── Mobile card list ── */
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {filteredRetail.length === 0 && (
                <div style={{ textAlign: 'center', padding: 32, color: '#9ca3af' }}>No users found</div>
              )}
              {filteredRetail.map(u => (
                <div key={u.userId} style={{
                  background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12,
                  padding: '14px 16px', boxShadow: '0 1px 4px rgba(0,0,0,0.04)'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 15 }}>{u.username}</div>
                      <div style={{ fontSize: 12, color: '#6b7280' }}>{u.email}</div>
                    </div>
                    <StatusBadge status={u.subscriptionStatus} />
                  </div>
                  <div style={{ display: 'flex', gap: 16, marginBottom: 10, fontSize: 13 }}>
                    <div><span style={{ color: '#9ca3af' }}>Plan: </span><strong>{u.planName || '—'}</strong></div>
                    {u.expiresAt && (
                      <div><span style={{ color: '#9ca3af' }}>Exp: </span><strong>{new Date(u.expiresAt).toLocaleDateString()}</strong></div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    <button onClick={() => setGrantRetailModal(u)} style={btnBlue}>Grant</button>
                    {u.subscriptionId && (
                      <>
                        <button onClick={() => setRenewRetailModal(u)} style={btnGreen}>Renew</button>
                        <button onClick={() => setChangePlanRetailModal(u)} style={btnOrange}>Change Plan</button>
                      </>
                    )}
                    {(u.subscriptionStatus === 'ACTIVE' || u.subscriptionStatus === 'GRACE_PERIOD') && (
                      <button onClick={() => handleSuspendRetail(u)} style={btnRed}>Suspend</button>
                    )}
                    {(u.subscriptionStatus === 'ACTIVE' || u.subscriptionStatus === 'GRACE_PERIOD' || u.subscriptionStatus === 'SUSPENDED') && (
                      <button onClick={() => setCancelRetailModal(u)} style={btnGray}>Cancel</button>
                    )}
                    <button onClick={() => setHistoryModal({ label: u.username, userId: u.userId, kind: 'retail' })} style={btnHistory}>History</button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            /* ── Desktop table ── */
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                <thead>
                  <tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                    <th style={th}>Username</th>
                    <th style={th}>Email</th>
                    <th style={th}>Plan</th>
                    <th style={th}>Status</th>
                    <th style={th}>Expires</th>
                    <th style={th}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRetail.map(u => (
                    <tr key={u.userId} style={{ borderBottom: '1px solid #f3f4f6' }}>
                      <td style={td}><strong>{u.username}</strong></td>
                      <td style={td}>{u.email}</td>
                      <td style={td}>{u.planName || '—'}</td>
                      <td style={td}><StatusBadge status={u.subscriptionStatus} /></td>
                      <td style={td}>{u.expiresAt ? new Date(u.expiresAt).toLocaleDateString() : '—'}</td>
                      <td style={td}>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          <button onClick={() => setGrantRetailModal(u)} style={btnBlue}>Grant</button>
                          {u.subscriptionId && (
                            <>
                              <button onClick={() => setRenewRetailModal(u)} style={btnGreen}>Renew</button>
                              <button onClick={() => setChangePlanRetailModal(u)} style={btnOrange}>Change Plan</button>
                            </>
                          )}
                          {(u.subscriptionStatus === 'ACTIVE' || u.subscriptionStatus === 'GRACE_PERIOD') && (
                            <button onClick={() => handleSuspendRetail(u)} style={btnRed}>Suspend</button>
                          )}
                          {(u.subscriptionStatus === 'ACTIVE' || u.subscriptionStatus === 'GRACE_PERIOD' || u.subscriptionStatus === 'SUSPENDED') && (
                            <button onClick={() => setCancelRetailModal(u)} style={btnGray}>Cancel</button>
                          )}
                          <button onClick={() => setHistoryModal({ label: u.username, userId: u.userId, kind: 'retail' })} style={btnHistory}>History</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {filteredRetail.length === 0 && (
                    <tr><td colSpan={6} style={{ textAlign: 'center', padding: 32, color: '#9ca3af' }}>No users found</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── TAB: Plans & Pricing ──────────────────────────────── */}
      {activeTab === 'plans' && (
        <div>
          {loadingPlans ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}>Loading…</div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: isMobile ? 20 : 32 }}>
              {/* Retail Plans */}
              <div>
                <h3 style={{ marginBottom: 16, fontSize: 16, fontWeight: 700 }}>🛒 Retail Plans</h3>
                <p style={{ fontSize: 12, color: '#6b7280', marginTop: -8, marginBottom: 12, lineHeight: 1.45 }}>
                  “Renew / upgrade payments” controls Stripe vs crypto on the retail upgrade/renew flow for each plan row.
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {retailPlans.map(p => (
                    <div key={p.id} style={{
                      background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10,
                      padding: '14px 16px', display: 'flex',
                      alignItems: isMobile ? 'flex-start' : 'center',
                      justifyContent: 'space-between',
                      flexDirection: isMobile ? 'column' : 'row',
                      gap: isMobile ? 10 : 0,
                    }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 700, fontSize: 15 }}>{p.name}</div>
                        <div style={{ color: '#6b7280', fontSize: 13 }}>
                          {formatRetailPlanCardSubtitle(p)}
                        </div>
                        <label style={{ display: 'block', marginTop: 10, fontSize: 12, color: '#6b7280' }}>
                          Renew / upgrade payments
                          <select
                            disabled={savingPlanPaymentKey === `retail-${p.id}`}
                            value={planRegistrationPaymentMethodsSelect(p)}
                            onChange={e => void handleRetailPlanPaymentMethods(p.id, e.target.value)}
                            style={{
                              marginTop: 4, display: 'block', width: '100%', maxWidth: 280,
                              padding: '6px 10px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 13,
                              background: '#fff',
                            }}
                          >
                            <option value="ALL">Stripe + Crypto</option>
                            <option value="STRIPE">Stripe only</option>
                            <option value="CRYPTO">Crypto only</option>
                          </select>
                        </label>
                      </div>
                      <button
                        onClick={() => setEditPriceModal({ plan: p, type: 'retail' })}
                        style={btnBlue}
                      >
                        Edit Price
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Admin Plans */}
              <div>
                <h3 style={{ marginBottom: 16, fontSize: 16, fontWeight: 700 }}>👥 Admin Plans</h3>
                <p style={{ fontSize: 12, color: '#6b7280', marginTop: -8, marginBottom: 12, lineHeight: 1.45 }}>
                  Base renewal options per plan (e.g. Starter). If an operator’s assigned products only allow crypto, the effective options at renewal can be narrower than here.
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {adminPlans.map(p => (
                    <div key={p.id} style={{
                      background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10,
                      padding: '14px 16px', display: 'flex',
                      alignItems: isMobile ? 'flex-start' : 'center',
                      justifyContent: 'space-between',
                      flexDirection: isMobile ? 'column' : 'row',
                      gap: isMobile ? 10 : 0,
                    }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 700, fontSize: 15 }}>{p.name}</div>
                        <div style={{ color: '#6b7280', fontSize: 13 }}>
                          €{Number(p.priceMonthly).toFixed(2)}/month · {p.maxStrategies} strategies
                          · {p.maxRetailUsers} retail users · {p.maxPositions} positions
                        </div>
                        <label style={{ display: 'block', marginTop: 10, fontSize: 12, color: '#6b7280' }}>
                          Operator self-service renewal payments
                          <select
                            disabled={savingPlanPaymentKey === `admin-${p.id}`}
                            value={planRegistrationPaymentMethodsSelect(p)}
                            onChange={e => void handleAdminPlanPaymentMethods(p.id, e.target.value)}
                            style={{
                              marginTop: 4, display: 'block', width: '100%', maxWidth: 280,
                              padding: '6px 10px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 13,
                              background: '#fff',
                            }}
                          >
                            <option value="ALL">Stripe + Crypto</option>
                            <option value="STRIPE">Stripe only</option>
                            <option value="CRYPTO">Crypto only</option>
                          </select>
                        </label>
                      </div>
                      <button
                        onClick={() => setEditPriceModal({ plan: p, type: 'admin' })}
                        style={btnBlue}
                      >
                        Edit Price
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── TAB: Revenue ──────────────────────────────────────── */}
      {activeTab === 'revenue' && (
        <RevenueTab
          loading={loadingRevenue}
          loadingPayments={loadingPayments}
          revenueSummary={revenueSummary}
          retailUsers={retailUsers}
          operators={operators}
          retailPlans={retailPlans}
          adminPlans={adminPlans}
          allPayments={allPayments}
          isMobile={isMobile}
          onRefresh={() => {
            loadRevenue();
            loadRetailUsers();
            loadOperators();
            loadAllPayments(retailUsers, operators);
          }}
        />
      )}

      {/* ── Modals ──────────────────────────────────────────────── */}

      {grantRetailModal && (
        <GrantModal
          title={`Grant Retail Subscription — ${grantRetailModal.username}`}
          plans={retailPlans}
          onConfirm={handleGrantRetail}
          onClose={() => setGrantRetailModal(null)}
        />
      )}

      {renewRetailModal && (
        <GrantModal
          title={`Renew Retail Subscription — ${renewRetailModal.username}`}
          plans={retailPlans}
          onConfirm={handleRenewRetail}
          onClose={() => setRenewRetailModal(null)}
        />
      )}

      {grantOpsModal && (
        <GrantModal
          title={`Grant Admin Subscription — ${grantOpsModal.username}`}
          plans={adminPlans}
          onConfirm={handleGrantOps}
          onClose={() => setGrantOpsModal(null)}
        />
      )}

      {renewOpsModal && (
        <GrantModal
          title={`Renew Admin Subscription — ${renewOpsModal.username}`}
          plans={adminPlans}
          onConfirm={handleRenewOps}
          onClose={() => setRenewOpsModal(null)}
        />
      )}

      {editPriceModal && (
        <EditPriceModal
          plan={editPriceModal.plan}
          onSave={handleSavePrice}
          onClose={() => setEditPriceModal(null)}
        />
      )}

      {/* R-F04: Change Plan modal — retail */}
      {changePlanRetailModal && (
        <ChangePlanModal
          title={`Change Plan — ${changePlanRetailModal.username}`}
          currentPlanName={changePlanRetailModal.planName}
          plans={retailPlans}
          onConfirm={handleChangePlanRetail}
          onClose={() => setChangePlanRetailModal(null)}
        />
      )}

      {/* R-F05: Change Plan modal — admin operators */}
      {changePlanOpsModal && (
        <ChangePlanModal
          title={`Change Admin Plan — ${changePlanOpsModal.username}`}
          currentPlanName={changePlanOpsModal.planName}
          plans={adminPlans}
          onConfirm={handleChangePlanOps}
          onClose={() => setChangePlanOpsModal(null)}
        />
      )}

      {/* R-F06: Cancel confirmation — retail */}
      {cancelRetailModal && (
        <CancelConfirmDialog
          name={cancelRetailModal.username}
          onConfirm={handleCancelRetail}
          onClose={() => setCancelRetailModal(null)}
        />
      )}

      {/* R-F06: Cancel confirmation — admin operators */}
      {cancelOpsModal && (
        <CancelConfirmDialog
          name={cancelOpsModal.username}
          onConfirm={handleCancelOps}
          onClose={() => setCancelOpsModal(null)}
        />
      )}

      {/* Subscription & Payment History modal */}
      {historyModal && (
        <SubscriptionHistoryModal
          label={historyModal.label}
          userId={historyModal.userId}
          kind={historyModal.kind}
          onClose={() => setHistoryModal(null)}
        />
      )}
    </div>
  );
};

// ─── Subscription History Modal ──────────────────────────────────────────────

interface SubHistoryRecord {
  id: number;
  plan: { name: string; priceMonthly: number; currency: string };
  status: string;
  startedAt: string;
  expiresAt: string;
  cancelledAt?: string | null;
  createdAt: string;
}

interface PaymentRecord {
  id: number;
  amount: number;
  currency: string;
  paymentDate: string;
  paymentMethod: string | null;
  transactionId: string | null;
  status: string;
  notes: string | null;
}

interface SubscriptionHistoryModalProps {
  label: string;
  userId: number;
  kind: 'retail' | 'admin';
  onClose: () => void;
}

function SubscriptionHistoryModal({ label, userId, kind, onClose }: SubscriptionHistoryModalProps) {
  const [subHistory, setSubHistory] = useState<SubHistoryRecord[]>([]);
  const [payments, setPayments] = useState<PaymentRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeInnerTab, setActiveInnerTab] = useState<'subscriptions' | 'payments'>('payments');

  useEffect(() => {
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const [histRes, payRes] = await Promise.all([
          kind === 'retail'
            ? adminApiService.getUserSubscriptionHistory(userId)
            : adminApiService.getAdminSubscriptionHistory(userId),
          kind === 'retail'
            ? adminApiService.getRetailUserPayments(userId)
            : adminApiService.getAdminOperatorPayments(userId),
        ]);
        if (histRes.success && histRes.data) setSubHistory(histRes.data as SubHistoryRecord[]);
        if (payRes.success && payRes.data) setPayments(payRes.data as PaymentRecord[]);
      } catch {
        setError('Failed to load history');
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [userId, kind]);

  const formatEur = (v: number) =>
    `€${Number(v).toLocaleString('en-IE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const fmtDate = (d: string) =>
    new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });

  const totalPaid = payments
    .filter(p => p.status === 'COMPLETED')
    .reduce((s, p) => s + Number(p.amount), 0);

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
      display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
      zIndex: 1000, overflowY: 'auto', padding: '24px 12px',
    }} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{
        background: '#fff', borderRadius: 14, width: '100%', maxWidth: 760,
        boxShadow: '0 24px 64px rgba(0,0,0,0.22)', overflow: 'hidden',
      }}>
        {/* Modal header */}
        <div style={{
          padding: '18px 24px', borderBottom: '1px solid #e5e7eb',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          background: '#f9fafb',
        }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>
              📋 Subscription History — {label}
            </h3>
            <p style={{ margin: '2px 0 0', fontSize: 12, color: '#6b7280' }}>
              {kind === 'retail' ? 'Retail User' : 'Admin Operator'} · All subscription and payment records
            </p>
          </div>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', cursor: 'pointer',
            fontSize: 20, color: '#6b7280', lineHeight: 1, padding: '4px 8px',
          }}>✕</button>
        </div>

        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>Loading…</div>
        ) : error ? (
          <div style={{ padding: 24, color: '#dc2626', fontSize: 14 }}>⚠️ {error}</div>
        ) : (
          <div style={{ padding: '0 0 24px' }}>

            {/* Revenue summary bar */}
            <div style={{
              display: 'flex', gap: 24, borderBottom: '1px solid #e5e7eb',
              background: '#fffbeb', padding: '12px 24px',
              flexWrap: 'wrap',
            }}>
              <div style={{ fontSize: 13 }}>
                <span style={{ color: '#9ca3af' }}>Total Paid: </span>
                <strong style={{ color: '#086afb', fontSize: 16 }}>{formatEur(totalPaid)}</strong>
              </div>
              <div style={{ fontSize: 13 }}>
                <span style={{ color: '#9ca3af' }}>Transactions: </span>
                <strong>{payments.filter(p => p.status === 'COMPLETED').length}</strong>
              </div>
              <div style={{ fontSize: 13 }}>
                <span style={{ color: '#9ca3af' }}>Subscription records: </span>
                <strong>{subHistory.length}</strong>
              </div>
            </div>

            {/* Inner tabs */}
            <div style={{
              display: 'flex', borderBottom: '1px solid #e5e7eb',
              overflowX: 'auto', WebkitOverflowScrolling: 'touch' as any,
            }}>
              {(['payments', 'subscriptions'] as const).map(t => (
                <button key={t} onClick={() => setActiveInnerTab(t)} style={{
                  padding: '10px 20px', border: 'none', background: 'none', cursor: 'pointer',
                  fontSize: 13, fontWeight: activeInnerTab === t ? 700 : 500,
                  color: activeInnerTab === t ? '#086afb' : '#6b7280',
                  borderBottom: activeInnerTab === t ? '3px solid #086afb' : '3px solid transparent',
                  whiteSpace: 'nowrap',
                }}>
                  {t === 'payments' ? `💳 Payment Transactions (${payments.length})` : `📄 Subscription Records (${subHistory.length})`}
                </button>
              ))}
            </div>

            <div style={{ padding: '16px 24px 0' }}>

              {/* Payment transactions */}
              {activeInnerTab === 'payments' && (
                payments.length === 0 ? (
                  <p style={{ color: '#9ca3af', fontSize: 14, textAlign: 'center', padding: '24px 0' }}>
                    No payment records found for this user.
                  </p>
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                      <thead>
                        <tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                          <th style={th}>Date</th>
                          <th style={{ ...th, textAlign: 'right' }}>Amount</th>
                          <th style={th}>Method</th>
                          <th style={th}>Ref / Tx ID</th>
                          <th style={th}>Status</th>
                          <th style={th}>Notes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {payments.map(p => (
                          <tr key={p.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                            <td style={td}>{fmtDate(p.paymentDate)}</td>
                            <td style={{ ...td, textAlign: 'right', fontWeight: 700, color: p.status === 'COMPLETED' ? '#086afb' : '#9ca3af' }}>
                              {formatEur(p.amount)} <span style={{ fontSize: 11, fontWeight: 400 }}>{p.currency}</span>
                            </td>
                            <td style={td}>{p.paymentMethod || '—'}</td>
                            <td style={{ ...td, fontSize: 11, color: '#6b7280', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {p.transactionId || '—'}
                            </td>
                            <td style={td}>
                              <span style={{
                                padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600,
                                background: p.status === 'COMPLETED' ? '#d1fae5' : p.status === 'FAILED' ? '#fee2e2' : '#f3f4f6',
                                color: p.status === 'COMPLETED' ? '#065f46' : p.status === 'FAILED' ? '#991b1b' : '#6b7280',
                              }}>{p.status}</span>
                            </td>
                            <td style={{ ...td, fontSize: 12, color: '#6b7280' }}>{p.notes || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr style={{ background: '#f9fafb', borderTop: '2px solid #e5e7eb' }}>
                          <td style={{ ...td, fontWeight: 700 }}>Total Paid</td>
                          <td style={{ ...td, textAlign: 'right', fontWeight: 800, color: '#086afb', fontSize: 15 }}>
                            {formatEur(totalPaid)}
                          </td>
                          <td colSpan={4} style={td} />
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )
              )}

              {/* Subscription records */}
              {activeInnerTab === 'subscriptions' && (
                subHistory.length === 0 ? (
                  <p style={{ color: '#9ca3af', fontSize: 14, textAlign: 'center', padding: '24px 0' }}>
                    No subscription records found.
                  </p>
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                      <thead>
                        <tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                          <th style={th}>Plan</th>
                          <th style={{ ...th, textAlign: 'right' }}>Price/mo</th>
                          <th style={th}>Status</th>
                          <th style={th}>Started</th>
                          <th style={th}>Expires / Expired</th>
                        </tr>
                      </thead>
                      <tbody>
                        {subHistory.map(s => (
                          <tr key={s.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                            <td style={td}><strong>{s.plan?.name ?? '—'}</strong></td>
                            <td style={{ ...td, textAlign: 'right' }}>
                              {s.plan ? formatEur(s.plan.priceMonthly) : '—'}
                            </td>
                            <td style={td}><StatusBadge status={s.status} /></td>
                            <td style={td}>{s.startedAt ? fmtDate(s.startedAt) : '—'}</td>
                            <td style={td}>
                              {s.cancelledAt
                                ? <span style={{ color: '#991b1b' }}>Cancelled {fmtDate(s.cancelledAt)}</span>
                                : s.expiresAt ? fmtDate(s.expiresAt) : '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )
              )}

            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Revenue Tab ─────────────────────────────────────────────────────────────

interface RevenueTabProps {
  loading: boolean;
  loadingPayments: boolean;
  revenueSummary: RevenueSummary | null;
  retailUsers: RetailUserRow[];
  operators: AdminOperatorRow[];
  retailPlans: SubscriptionPlan[];
  adminPlans: AdminSubscriptionPlan[];
  allPayments: AllPaymentRow[];
  isMobile: boolean;
  onRefresh: () => void;
}

function RevenueTab({ loading, loadingPayments, revenueSummary, retailUsers, operators, retailPlans, adminPlans, allPayments, isMobile, onRefresh }: RevenueTabProps) {
  const formatEur = (v: number) => `€${v.toLocaleString('en-IE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  // ── Derived revenue breakdown per plan ──────────────────────────────────────
  // Build a map of planName → price from the loaded plans
  const retailPriceMap = new Map(retailPlans.map(p => [p.name, p.priceMonthly]));
  const adminPriceMap  = new Map(adminPlans.map(p => [p.name, p.priceMonthly]));

  interface PlanRevenueRow {
    type: 'Retail' | 'Admin';
    planName: string;
    priceMonthly: number;
    activeCount: number;
    gracePeriodCount: number;
    suspendedCount: number;
    totalSubscribers: number;
    monthlyRevenue: number;
  }

  // Group retail users by plan
  const retailByPlan = new Map<string, { active: number; grace: number; suspended: number }>();
  retailUsers.forEach(u => {
    if (!u.planName) return;
    const existing = retailByPlan.get(u.planName) || { active: 0, grace: 0, suspended: 0 };
    if (u.subscriptionStatus === 'ACTIVE')        existing.active++;
    else if (u.subscriptionStatus === 'GRACE_PERIOD') existing.grace++;
    else if (u.subscriptionStatus === 'SUSPENDED')    existing.suspended++;
    retailByPlan.set(u.planName, existing);
  });

  // Group operator users by plan
  const adminByPlan = new Map<string, { active: number; grace: number; suspended: number }>();
  operators.forEach(o => {
    if (!o.planName) return;
    const existing = adminByPlan.get(o.planName) || { active: 0, grace: 0, suspended: 0 };
    if (o.subscriptionStatus === 'ACTIVE')        existing.active++;
    else if (o.subscriptionStatus === 'GRACE_PERIOD') existing.grace++;
    else if (o.subscriptionStatus === 'SUSPENDED')    existing.suspended++;
    adminByPlan.set(o.planName, existing);
  });

  const planRevenueRows: PlanRevenueRow[] = [];

  retailByPlan.forEach((counts, planName) => {
    const price = Number(retailPriceMap.get(planName) ?? 0);
    const total = counts.active + counts.grace + counts.suspended;
    planRevenueRows.push({
      type: 'Retail',
      planName,
      priceMonthly: price,
      activeCount: counts.active,
      gracePeriodCount: counts.grace,
      suspendedCount: counts.suspended,
      totalSubscribers: total,
      monthlyRevenue: price * counts.active,
    });
  });

  adminByPlan.forEach((counts, planName) => {
    const price = Number(adminPriceMap.get(planName) ?? 0);
    const total = counts.active + counts.grace + counts.suspended;
    planRevenueRows.push({
      type: 'Admin',
      planName,
      priceMonthly: price,
      activeCount: counts.active,
      gracePeriodCount: counts.grace,
      suspendedCount: counts.suspended,
      totalSubscribers: total,
      monthlyRevenue: price * counts.active,
    });
  });

  const computedRetailMrr = planRevenueRows
    .filter(r => r.type === 'Retail')
    .reduce((sum, r) => sum + r.monthlyRevenue, 0);
  const computedAdminMrr = planRevenueRows
    .filter(r => r.type === 'Admin')
    .reduce((sum, r) => sum + r.monthlyRevenue, 0);
  const computedTotalMrr = computedRetailMrr + computedAdminMrr;

  // Prefer API-returned MRR if present, fall back to computed
  const adminMrr   = revenueSummary?.adminMrr  ?? computedAdminMrr;
  const retailMrr  = revenueSummary?.retailMrr ?? computedRetailMrr;
  const totalMrr   = revenueSummary?.totalMrr  ?? computedTotalMrr;
  const atRisk     = revenueSummary?.atRiskCount ??
    (revenueSummary ? (revenueSummary.adminGraceCount ?? 0) + (revenueSummary.retailGraceCount ?? 0) : 0);

  // ── Actual collected revenue from real payment records ───────────────────────
  const completedPayments = allPayments.filter(p => p.status === 'COMPLETED');
  const totalCollected    = completedPayments.reduce((s, p) => s + Number(p.amount), 0);
  const thisMonth         = new Date().toISOString().slice(0, 7); // 'YYYY-MM'
  const collectedThisMonth = completedPayments
    .filter(p => p.paymentDate?.slice(0, 7) === thisMonth)
    .reduce((s, p) => s + Number(p.amount), 0);

  // ── Reconciliation: accounts expiring within 30 days or at-risk ─────────────
  const now = new Date();
  const in30 = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);

  interface ReconciliationRow {
    kind: 'Retail' | 'Admin';
    username: string;
    email: string;
    planName: string | null;
    status: string;
    expiresAt: string | null;
    daysUntilExpiry: number | null;
  }

  const reconciliationRows: ReconciliationRow[] = [];

  retailUsers.forEach(u => {
    if (!['ACTIVE', 'GRACE_PERIOD', 'SUSPENDED'].includes(u.subscriptionStatus)) return;
    const expiry = u.expiresAt ? new Date(u.expiresAt) : null;
    const days = expiry ? Math.ceil((expiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)) : null;
    const expiringSoon = expiry && expiry <= in30;
    const atRiskStatus = u.subscriptionStatus === 'GRACE_PERIOD' || u.subscriptionStatus === 'SUSPENDED';
    if (expiringSoon || atRiskStatus) {
      reconciliationRows.push({
        kind: 'Retail',
        username: u.username,
        email: u.email,
        planName: u.planName,
        status: u.subscriptionStatus,
        expiresAt: u.expiresAt,
        daysUntilExpiry: days,
      });
    }
  });

  operators.forEach(o => {
    if (!['ACTIVE', 'GRACE_PERIOD', 'SUSPENDED'].includes(o.subscriptionStatus)) return;
    const expiry = o.expiresAt ? new Date(o.expiresAt) : null;
    const days = expiry ? Math.ceil((expiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)) : null;
    const expiringSoon = expiry && expiry <= in30;
    const atRiskStatus = o.subscriptionStatus === 'GRACE_PERIOD' || o.subscriptionStatus === 'SUSPENDED';
    if (expiringSoon || atRiskStatus) {
      reconciliationRows.push({
        kind: 'Admin',
        username: o.username,
        email: o.email,
        planName: o.planName,
        status: o.subscriptionStatus,
        expiresAt: o.expiresAt,
        daysUntilExpiry: days,
      });
    }
  });

  // Sort: grace/suspended first, then by days until expiry ascending
  reconciliationRows.sort((a, b) => {
    const priority = (s: string) => s === 'SUSPENDED' ? 0 : s === 'GRACE_PERIOD' ? 1 : 2;
    const pd = priority(a.status) - priority(b.status);
    if (pd !== 0) return pd;
    return (a.daysUntilExpiry ?? 999) - (b.daysUntilExpiry ?? 999);
  });

  if (loading) {
    return <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}>Loading…</div>;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>

      {/* Refresh */}
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button onClick={onRefresh} style={{
          padding: '8px 16px', borderRadius: 20, background: '#f3f4f6',
          border: '1px solid #d1d5db', cursor: 'pointer', fontSize: 13
        }}>🔄 Refresh</button>
      </div>

      {/* ── MRR Stat Cards ─────────────────────────────────────────────────── */}
      <section>
        <h3 style={{ margin: '0 0 14px', fontSize: 15, fontWeight: 700, color: '#111827' }}>
          💰 Monthly Recurring Revenue (MRR)
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(auto-fit, minmax(${isMobile ? '140px' : '180px'}, 1fr))`, gap: 16 }}>
          <MrrCard label="Total MRR (Projected)" value={formatEur(totalMrr)} color="#086afb"
            sub={collectedThisMonth > 0 ? `Collected this month: ${formatEur(collectedThisMonth)}` : 'All active subscriptions'} />
          <MrrCard label="Retail MRR" value={formatEur(retailMrr)} color="#0dca9d"
            sub={`${revenueSummary?.activeRetailSubscribers ?? 0} active subscribers`} />
          <MrrCard label="Admin MRR" value={formatEur(adminMrr)} color="#e7038e"
            sub={`${revenueSummary?.activeAdminSubscribers ?? 0} active operators`} />
          <MrrCard label="All-Time Collected" value={allPayments.length > 0 ? formatEur(totalCollected) : '—'} color="#0dca9d"
            sub={`${completedPayments.length} completed payments`} />
          <MrrCard label="At Risk" value={String(atRisk)} color="#ff7171"
            sub="Grace period / suspended" isCount />
        </div>
        <p style={{ marginTop: 8, fontSize: 11, color: '#9ca3af' }}>
          * MRR is calculated from active subscriptions × monthly plan price. Grace period and suspended subscribers are excluded from revenue.
        </p>
      </section>

      {/* ── Revenue Breakdown by Plan ───────────────────────────────────────── */}
      <section>
        <h3 style={{ margin: '0 0 14px', fontSize: 15, fontWeight: 700, color: '#111827' }}>
          📋 Revenue Breakdown by Plan
        </h3>
        {planRevenueRows.length === 0 ? (
          <p style={{ color: '#9ca3af', fontSize: 14 }}>No subscription data available.</p>
        ) : isMobile ? (
          /* ── Mobile: stacked cards ── */
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {planRevenueRows.map((row, i) => (
              <div key={i} style={{
                background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10,
                padding: '14px 16px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                borderLeft: `4px solid ${row.type === 'Retail' ? '#086afb' : '#e7038e'}`,
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 14 }}>{row.planName}</div>
                    <span style={{
                      marginTop: 2, display: 'inline-block',
                      padding: '1px 8px', borderRadius: 8, fontSize: 10, fontWeight: 600,
                      background: row.type === 'Retail' ? '#eff6ff' : '#fdf4ff',
                      color: row.type === 'Retail' ? '#1d4ed8' : '#7e22ce',
                    }}>{row.type}</span>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 18, fontWeight: 800, color: '#086afb' }}>{formatEur(row.monthlyRevenue)}</div>
                    <div style={{ fontSize: 11, color: '#9ca3af' }}>monthly revenue</div>
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 16px', fontSize: 13 }}>
                  <div><span style={{ color: '#9ca3af' }}>Price/mo: </span><strong>{formatEur(row.priceMonthly)}</strong></div>
                  <div><span style={{ color: '#9ca3af' }}>Active: </span><strong style={{ color: '#065f46' }}>{row.activeCount}</strong></div>
                  <div><span style={{ color: '#9ca3af' }}>Grace: </span>
                    <strong style={{ color: row.gracePeriodCount > 0 ? '#92400e' : '#6b7280' }}>{row.gracePeriodCount}</strong>
                  </div>
                  <div><span style={{ color: '#9ca3af' }}>Suspended: </span>
                    <strong style={{ color: row.suspendedCount > 0 ? '#9a3412' : '#6b7280' }}>{row.suspendedCount}</strong>
                  </div>
                </div>
              </div>
            ))}
            {/* Mobile totals bar */}
            <div style={{
              background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10,
              padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#374151' }}>
                Total Active: {planRevenueRows.reduce((s, r) => s + r.activeCount, 0)}
              </div>
              <div style={{ fontSize: 17, fontWeight: 800, color: '#086afb' }}>{formatEur(totalMrr)}</div>
            </div>
          </div>
        ) : (
          /* ── Desktop: full table ── */
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead>
                <tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                  <th style={th}>Type</th>
                  <th style={th}>Plan Name</th>
                  <th style={{ ...th, textAlign: 'right' }}>Price/mo</th>
                  <th style={{ ...th, textAlign: 'right' }}>Active</th>
                  <th style={{ ...th, textAlign: 'right' }}>Grace</th>
                  <th style={{ ...th, textAlign: 'right' }}>Suspended</th>
                  <th style={{ ...th, textAlign: 'right' }}>Monthly Revenue</th>
                </tr>
              </thead>
              <tbody>
                {planRevenueRows.map((row, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={td}>
                      <span style={{
                        padding: '2px 10px', borderRadius: 10, fontSize: 11, fontWeight: 600,
                        background: row.type === 'Retail' ? '#eff6ff' : '#fdf4ff',
                        color: row.type === 'Retail' ? '#1d4ed8' : '#7e22ce',
                      }}>{row.type}</span>
                    </td>
                    <td style={td}><strong>{row.planName}</strong></td>
                    <td style={{ ...td, textAlign: 'right' }}>{formatEur(row.priceMonthly)}</td>
                    <td style={{ ...td, textAlign: 'right' }}>
                      <span style={{ fontWeight: 700, color: '#065f46' }}>{row.activeCount}</span>
                    </td>
                    <td style={{ ...td, textAlign: 'right' }}>
                      <span style={{ color: row.gracePeriodCount > 0 ? '#92400e' : '#6b7280' }}>
                        {row.gracePeriodCount}
                      </span>
                    </td>
                    <td style={{ ...td, textAlign: 'right' }}>
                      <span style={{ color: row.suspendedCount > 0 ? '#9a3412' : '#6b7280' }}>
                        {row.suspendedCount}
                      </span>
                    </td>
                    <td style={{ ...td, textAlign: 'right', fontWeight: 700, color: '#086afb' }}>
                      {formatEur(row.monthlyRevenue)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ background: '#f9fafb', borderTop: '2px solid #e5e7eb' }}>
                  <td colSpan={3} style={{ ...td, fontWeight: 700 }}>Total</td>
                  <td style={{ ...td, textAlign: 'right', fontWeight: 700 }}>
                    {planRevenueRows.reduce((s, r) => s + r.activeCount, 0)}
                  </td>
                  <td style={{ ...td, textAlign: 'right', fontWeight: 700 }}>
                    {planRevenueRows.reduce((s, r) => s + r.gracePeriodCount, 0)}
                  </td>
                  <td style={{ ...td, textAlign: 'right', fontWeight: 700 }}>
                    {planRevenueRows.reduce((s, r) => s + r.suspendedCount, 0)}
                  </td>
                  <td style={{ ...td, textAlign: 'right', fontWeight: 800, color: '#086afb', fontSize: 15 }}>
                    {formatEur(totalMrr)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </section>

      {/* ── Reconciliation: Expiring Soon / At-Risk ─────────────────────────── */}
      <section>
        <h3 style={{ margin: '0 0 6px', fontSize: 15, fontWeight: 700, color: '#111827' }}>
          ⚠️ Reconciliation — Expiring Soon &amp; At-Risk Accounts
        </h3>
        <p style={{ margin: '0 0 14px', fontSize: 13, color: '#6b7280' }}>
          Subscriptions expiring within 30 days, or currently in grace period / suspended.
        </p>
        {reconciliationRows.length === 0 ? (
          <div style={{
            background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10,
            padding: '18px 20px', color: '#166534', fontSize: 14
          }}>
            ✅ No accounts expiring within 30 days. All subscriptions look healthy.
          </div>
        ) : isMobile ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {reconciliationRows.map((row, i) => (
              <div key={i} style={{
                background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10,
                padding: '12px 14px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
                  <div>
                    <span style={{ fontWeight: 700, fontSize: 14 }}>{row.username}</span>
                    <span style={{
                      marginLeft: 8, padding: '1px 8px', borderRadius: 8, fontSize: 10, fontWeight: 600,
                      background: row.kind === 'Retail' ? '#eff6ff' : '#fdf4ff',
                      color: row.kind === 'Retail' ? '#1d4ed8' : '#7e22ce',
                    }}>{row.kind}</span>
                  </div>
                  <StatusBadge status={row.status} />
                </div>
                <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 6 }}>{row.email}</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 12px', fontSize: 13 }}>
                  <div><span style={{ color: '#9ca3af' }}>Plan: </span><strong>{row.planName || '—'}</strong></div>
                  <div>
                    <span style={{ color: '#9ca3af' }}>Days left: </span>
                    <strong style={{ color: (row.daysUntilExpiry ?? 99) <= 7 ? '#dc2626' : (row.daysUntilExpiry ?? 99) <= 14 ? '#d97706' : '#374151' }}>
                      {row.daysUntilExpiry !== null ? (row.daysUntilExpiry <= 0 ? 'Expired' : `${row.daysUntilExpiry}d`) : '—'}
                    </strong>
                  </div>
                  {row.expiresAt && (
                    <div style={{ gridColumn: '1 / -1' }}>
                      <span style={{ color: '#9ca3af' }}>Expires: </span>
                      <strong style={{ color: '#374151' }}>
                        {new Date(row.expiresAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                      </strong>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead>
                <tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                  <th style={th}>Type</th>
                  <th style={th}>Username</th>
                  <th style={th}>Email</th>
                  <th style={th}>Plan</th>
                  <th style={th}>Status</th>
                  <th style={th}>Expiry Date</th>
                  <th style={{ ...th, textAlign: 'right' }}>Days Left</th>
                </tr>
              </thead>
              <tbody>
                {reconciliationRows.map((row, i) => {
                  const urgent = (row.daysUntilExpiry ?? 99) <= 7;
                  const warning = !urgent && (row.daysUntilExpiry ?? 99) <= 14;
                  return (
                    <tr key={i} style={{
                      borderBottom: '1px solid #f3f4f6',
                      background: urgent ? '#fff5f5' : warning ? '#fffbeb' : 'transparent',
                    }}>
                      <td style={td}>
                        <span style={{
                          padding: '2px 10px', borderRadius: 10, fontSize: 11, fontWeight: 600,
                          background: row.kind === 'Retail' ? '#eff6ff' : '#fdf4ff',
                          color: row.kind === 'Retail' ? '#1d4ed8' : '#7e22ce',
                        }}>{row.kind}</span>
                      </td>
                      <td style={td}><strong>{row.username}</strong></td>
                      <td style={td}>{row.email}</td>
                      <td style={td}>{row.planName || '—'}</td>
                      <td style={td}><StatusBadge status={row.status} /></td>
                      <td style={td}>
                        {row.expiresAt ? new Date(row.expiresAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                      </td>
                      <td style={{ ...td, textAlign: 'right' }}>
                        {row.daysUntilExpiry !== null ? (
                          <span style={{
                            fontWeight: 700,
                            color: urgent ? '#dc2626' : warning ? '#d97706' : '#374151',
                          }}>
                            {row.daysUntilExpiry <= 0 ? 'Expired' : `${row.daysUntilExpiry}d`}
                          </span>
                        ) : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* ── Transaction Ledger (actual collected payments) ──────────────────── */}
      <section>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 14, flexWrap: 'wrap', gap: 8 }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#111827' }}>
              💳 Transaction Ledger — All Collected Payments
            </h3>
            <p style={{ margin: '3px 0 0', fontSize: 12, color: '#6b7280' }}>
              Every payment recorded across all retail users and admin operators, including historical plan changes.
            </p>
          </div>
          {allPayments.length > 0 && (
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 11, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.04em' }}>This month</div>
                <div style={{ fontSize: 18, fontWeight: 800, color: '#086afb' }}>{formatEur(collectedThisMonth)}</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 11, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.04em' }}>All time</div>
                <div style={{ fontSize: 18, fontWeight: 800, color: '#0dca9d' }}>{formatEur(totalCollected)}</div>
              </div>
            </div>
          )}
        </div>

        {loadingPayments ? (
          <div style={{ textAlign: 'center', padding: 32, color: '#9ca3af', fontSize: 14 }}>
            Loading payment records…
          </div>
        ) : allPayments.length === 0 ? (
          <div style={{
            background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10,
            padding: '20px 24px', color: '#9ca3af', fontSize: 14, textAlign: 'center'
          }}>
            No payment records found. Payments are recorded when you manually log a payment via the History button on a user row.
          </div>
        ) : isMobile ? (
          /* ── Mobile cards ── */
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {allPayments.map((p, i) => (
              <div key={i} style={{
                background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10,
                padding: '12px 14px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                borderLeft: `4px solid ${p.status === 'COMPLETED' ? '#086afb' : p.status === 'FAILED' ? '#ff7171' : '#e5e7eb'}`,
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
                  <div>
                    <span style={{ fontWeight: 700, fontSize: 14 }}>{p.username}</span>
                    <span style={{
                      marginLeft: 8, padding: '1px 8px', borderRadius: 8, fontSize: 10, fontWeight: 600,
                      background: p.kind === 'Retail' ? '#eff6ff' : '#fdf4ff',
                      color: p.kind === 'Retail' ? '#1d4ed8' : '#7e22ce',
                    }}>{p.kind}</span>
                  </div>
                  <div style={{ fontSize: 16, fontWeight: 800, color: p.status === 'COMPLETED' ? '#086afb' : '#9ca3af' }}>
                    {formatEur(p.amount)}
                  </div>
                </div>
                <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>{p.email}</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 12px', fontSize: 12 }}>
                  <div><span style={{ color: '#9ca3af' }}>Date: </span><strong>{new Date(p.paymentDate).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' })}</strong></div>
                  <div>
                    <span style={{
                      padding: '1px 8px', borderRadius: 8, fontSize: 11, fontWeight: 600,
                      background: p.status === 'COMPLETED' ? '#d1fae5' : p.status === 'FAILED' ? '#fee2e2' : '#f3f4f6',
                      color: p.status === 'COMPLETED' ? '#065f46' : p.status === 'FAILED' ? '#991b1b' : '#6b7280',
                    }}>{p.status}</span>
                  </div>
                  {p.paymentMethod && <div><span style={{ color: '#9ca3af' }}>Method: </span>{p.paymentMethod}</div>}
                  {p.transactionId && <div style={{ gridColumn: '1/-1', fontSize: 11, color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>Ref: {p.transactionId}</div>}
                </div>
              </div>
            ))}
          </div>
        ) : (
          /* ── Desktop table ── */
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead>
                <tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                  <th style={th}>Date</th>
                  <th style={th}>Type</th>
                  <th style={th}>User</th>
                  <th style={th}>Email</th>
                  <th style={{ ...th, textAlign: 'right' }}>Amount</th>
                  <th style={th}>Method</th>
                  <th style={th}>Status</th>
                  <th style={th}>Ref / Tx ID</th>
                  <th style={th}>Notes</th>
                </tr>
              </thead>
              <tbody>
                {allPayments.map((p, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>
                      {new Date(p.paymentDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                    </td>
                    <td style={td}>
                      <span style={{
                        padding: '2px 10px', borderRadius: 10, fontSize: 11, fontWeight: 600,
                        background: p.kind === 'Retail' ? '#eff6ff' : '#fdf4ff',
                        color: p.kind === 'Retail' ? '#1d4ed8' : '#7e22ce',
                      }}>{p.kind}</span>
                    </td>
                    <td style={td}><strong>{p.username}</strong></td>
                    <td style={{ ...td, fontSize: 12, color: '#6b7280' }}>{p.email}</td>
                    <td style={{ ...td, textAlign: 'right', fontWeight: 700, color: p.status === 'COMPLETED' ? '#086afb' : '#9ca3af' }}>
                      {formatEur(p.amount)} <span style={{ fontSize: 11, fontWeight: 400, color: '#9ca3af' }}>{p.currency}</span>
                    </td>
                    <td style={td}>{p.paymentMethod || '—'}</td>
                    <td style={td}>
                      <span style={{
                        padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600,
                        background: p.status === 'COMPLETED' ? '#d1fae5' : p.status === 'FAILED' ? '#fee2e2' : '#f3f4f6',
                        color: p.status === 'COMPLETED' ? '#065f46' : p.status === 'FAILED' ? '#991b1b' : '#6b7280',
                      }}>{p.status}</span>
                    </td>
                    <td style={{ ...td, fontSize: 11, color: '#6b7280', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {p.transactionId || '—'}
                    </td>
                    <td style={{ ...td, fontSize: 12, color: '#6b7280' }}>{p.notes || '—'}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ background: '#f9fafb', borderTop: '2px solid #e5e7eb' }}>
                  <td colSpan={4} style={{ ...td, fontWeight: 700 }}>
                    Total Collected ({completedPayments.length} payments)
                  </td>
                  <td style={{ ...td, textAlign: 'right', fontWeight: 800, color: '#086afb', fontSize: 15 }}>
                    {formatEur(totalCollected)}
                  </td>
                  <td colSpan={4} style={td} />
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </section>

      {/* ── Subscriber Count Summary ─────────────────────────────────────────── */}
      <section>
        <h3 style={{ margin: '0 0 14px', fontSize: 15, fontWeight: 700, color: '#111827' }}>
          👥 Subscriber Count Summary
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(auto-fit, minmax(${isMobile ? '140px' : '160px'}, 1fr))`, gap: 16 }}>
          <StatCard label="Active Retail" value={revenueSummary?.activeRetailSubscribers ?? 0} color="#086afb" />
          <StatCard label="Active Admins" value={revenueSummary?.activeAdminSubscribers ?? 0} color="#0dca9d" />
          <StatCard label="Retail Plans" value={revenueSummary?.retailPlans ?? retailPlans.length} color="#e7038e" />
          <StatCard label="Admin Plans" value={revenueSummary?.adminPlans ?? adminPlans.length} color="#fbb445" />
        </div>
      </section>

    </div>
  );
}

// ─── MRR Stat Card ────────────────────────────────────────────────────────────

function MrrCard({ label, value, color, sub, isCount }: {
  label: string; value: string; color: string; sub?: string; isCount?: boolean;
}) {
  return (
    <div style={{
      background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12,
      padding: '18px 20px', boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
      borderTop: `3px solid ${color}`,
    }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
        {label}
      </div>
      <div style={{ fontSize: isCount ? 32 : 24, fontWeight: 800, color, lineHeight: 1.2 }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

// ─── Small stat card ──────────────────────────────────────────────────────────

function StatCard({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div style={{
      background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12,
      padding: '20px 24px', textAlign: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.05)'
    }}>
      <div style={{ fontSize: 36, fontWeight: 800, color }}>{value}</div>
      <div style={{ fontSize: 13, color: '#6b7280', marginTop: 4 }}>{label}</div>
    </div>
  );
}

// ─── Shared cell styles ───────────────────────────────────────────────────────

const th: React.CSSProperties = {
  padding: '10px 14px', textAlign: 'left', fontSize: 12,
  fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.05em'
};

const td: React.CSSProperties = {
  padding: '10px 14px', verticalAlign: 'middle'
};

const btnBlue: React.CSSProperties = {
  padding: '5px 14px', borderRadius: 16, border: 'none',
  background: '#086afb', color: '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 600
};

const btnGreen: React.CSSProperties = {
  padding: '5px 14px', borderRadius: 16, border: 'none',
  background: '#0dca9d', color: '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 600
};

const btnRed: React.CSSProperties = {
  padding: '5px 14px', borderRadius: 16, border: 'none',
  background: '#ff7171', color: '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 600
};

const btnOrange: React.CSSProperties = {
  padding: '5px 14px', borderRadius: 16, border: 'none',
  background: '#fbb445', color: '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 600
};

const btnGray: React.CSSProperties = {
  padding: '5px 14px', borderRadius: 16, border: '1px solid #d1d5db',
  background: '#f9fafb', color: '#6b7280', cursor: 'pointer', fontSize: 12, fontWeight: 600
};

const btnHistory: React.CSSProperties = {
  padding: '5px 14px', borderRadius: 16, border: '1px solid #c7d2fe',
  background: '#eef2ff', color: '#4338ca', cursor: 'pointer', fontSize: 12, fontWeight: 600
};

export default SuperAdminSubscriptions;
